import { Spinner } from '@packages/ui/components/Spinner';
import { fetchOrder } from 'actions/entities/orders/fetchOrder';
import { setGatewayOrigin, setGatewaySource } from 'actions/ui/ui';
import { OrderNotFound } from 'components/OrderNotFound/OrderNotFound';
import { RedirectToPay } from 'components/Routes/RedirectToPay/RedirectToPay';
import { FF_PAYBYLINK_DUPLICATE_PLAN_FIX } from 'constants/features';
import { GATEWAY_DUPLICATED_ROUTE } from 'constants/routes';
import type { PaymentSource } from 'hooks/usePayment/PaymentSourceContext';
import { usePayment } from 'hooks/usePayment/usePayment';
import { usePeertransferFlags } from 'hooks/usePeertransferFlags/usePeertransferFlags';
import isEmpty from 'lodash/isEmpty';
import { useEffect, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import { Navigate, useParams, useSearchParams } from 'react-router-dom';
import type { RootState } from 'reducers/types';
import {
  getOrder,
  getRecurringReceivableId,
} from 'selectors/entities/order/order';
import { useDispatch } from 'store/configureStore';

function PrefillOrderAndPay() {
  const [isFetching, setIsFetching] = useState(true);
  const [searchParams] = useSearchParams();
  const dispatch = useDispatch();
  const { orderId } = useParams<{ orderId: string }>();
  const paymentSource = useMemo<PaymentSource>(
    () => ({
      type: 'order',
      orderId: orderId ?? '',
      token: searchParams.get('token') ?? '',
    }),
    [orderId, searchParams],
  );
  const { data: payment, fetchPaymentByOrder } = usePayment(paymentSource);
  const hasPayment = !!payment;
  const order = useSelector((state: RootState) => getOrder(state));
  const recurringReceivableId = useSelector((state: RootState) =>
    getRecurringReceivableId(state),
  );
  const { data: flags, isLoading: isFlagsLoading } = usePeertransferFlags(
    FF_PAYBYLINK_DUPLICATE_PLAN_FIX,
  );
  const isDuplicatePlanFixEnabled = !!flags?.[FF_PAYBYLINK_DUPLICATE_PLAN_FIX];

  useEffect(() => {
    const fetchData = async () => {
      const token = searchParams.get('token');
      const platform = searchParams.get('platform');

      dispatch(setGatewayOrigin());
      if (platform) {
        dispatch(setGatewaySource(platform));
      }

      try {
        if (!orderId || !token) {
          return;
        }

        await dispatch(fetchOrder({ orderId, token }));
        await fetchPaymentByOrder({ orderId, token });
      } finally {
        setIsFetching(false);
      }
    };

    fetchData();
  }, [dispatch, fetchPaymentByOrder, orderId, searchParams]);

  if (isFetching || isFlagsLoading) {
    return <Spinner />;
  }
  if (hasPayment || (isDuplicatePlanFixEnabled && recurringReceivableId)) {
    return <Navigate to={GATEWAY_DUPLICATED_ROUTE} />;
  }

  return isEmpty(order) ? (
    <OrderNotFound />
  ) : (
    <RedirectToPay cleanSearch={true} />
  );
}

export { PrefillOrderAndPay };
